import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Text, StylovyzeFormV2, Button } from '@innovyze/stylovyze';
import { IconButton, Stack } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { FullPageWrapper } from '@Utils/styles';
import { selectStandardCodes } from '@Selectors/inspections.selector';

import { selectEventTypes, selectTaskTypes, selectRehabMetadataLoading } from '@Selectors/rehab.selectors';
import { fetchFieldMetadataAction, clearFieldMetadataAction } from '@Actions/rehab.actions';
import { FormikHelpers, FormikValues } from 'formik';
import { ROUTES } from '@Pages/PageRoutes';
import { fetchInspectionStandardCodes } from '@Actions/inspections.actions';
import { addRouteNamespace } from '@Utils/actions';
import { isAlphanumericName, isBlankName, isDuplicateName } from '@Utils/nameValidation';
import { RenameDialog } from '@Components/failureDefinition/RenameDialog';
import EditIcon from '@mui/icons-material/Edit';
import {
	upsertFailureDefinition,
	getFailureDefinitionAction,
	listAllFailureDefinitions,
	renameFailureDefinition,
} from '@Actions/failureDefinition.actions';
import { CreateFailureDefinitionRequest, Inspections, FailureDefinition as FailureDefinitionType } from '@Types';
import { pacpFieldMappings, wsaFieldMappings } from '@Types/inspections.types';
import { FailureDefinitionConfiguration } from '@Components/failureDefinition/FailureDefinitionConfiguration';
import { FailureDefinitionResultsTab } from '@Components/failureDefinition/FailureDefinitionResultsTab';
import {
	selectFailureDefinitionsCards,
	selectFailureDefinition,
	selectFailureDefinitionProcess,
} from '@Selectors/failureDefinitions.selector';

export interface FailureDefinitionFormValues {
	id?: string;
	failureDefinitionName: string;
	systemType: string;
	assetType: string;
	inspectionStandard: string;
	inspections: string[];
	events: string[];
	eventDateMap: Record<string, string>;
	tasks: string[];
	taskDateMap: Record<string, string>;
	conditionScoreKey: string;
	conditionScoreOperator: string;
	conditionScoreValue: number;
	conditionScoreValueTo: number;
	defectCodes: string[];
	prioritizedCategory?: string;
}

const defaultModel: FailureDefinitionFormValues = {
	failureDefinitionName: 'Untitled Failure Definition',
	systemType: '',
	assetType: '',
	inspectionStandard: 'PACP',
	inspections: [],
	events: [],
	eventDateMap: {},
	tasks: [],
	taskDateMap: {},
	conditionScoreKey: '',
	conditionScoreOperator: '',
	conditionScoreValue: 0,
	conditionScoreValueTo: 0,
	defectCodes: [],
	prioritizedCategory: '',
};

export const FailureDefinition = () => {
	const { t } = useGlobalization();
	const history = useHistory();
	const { state: draft } = useLocation<FailureDefinitionFormValues | undefined>();
	const { id } = useParams<{ id?: string }>();
	const urlBasedCreating = id === undefined || id === 'new';
	const [editingMode, setEditingMode] = useState(!urlBasedCreating);
	const [isRenameOpen, setIsRenameOpen] = useState(false);
	const [currentTabIndex, setCurrentTabIndex] = useState(0);
	const [model, setModel] = useState<FailureDefinitionFormValues>({
		...defaultModel,
		...draft,
	});
	const [loadedFailureDefinition, setLoadedFailureDefinition] = useState<FailureDefinitionType | null>(null);
	const [hasAutoSwitchedToResults, setHasAutoSwitchedToResults] = useState(false);

	const dispatch = useDispatch();
	const failureDefinitionsCards = selectFailureDefinitionsCards();
	const individualFailureDefinition = selectFailureDefinition();
	const { isUpsertInProcess, isRunInProcess } = selectFailureDefinitionProcess();
	const isRunning = isUpsertInProcess || isRunInProcess;

	const [createdFailureDefinitionId, setCreatedFailureDefinitionId] = useState<string | null>(null);

	const failureDefinitionFromCards = useMemo(() => {
		if (editingMode && id && failureDefinitionsCards?.failureDefinitions) {
			return failureDefinitionsCards.failureDefinitions.find(card => card.id === id) || null;
		}
		return null;
	}, [editingMode, id, failureDefinitionsCards?.failureDefinitions]);

	useEffect(() => {
		if (editingMode && id) {
			if (failureDefinitionFromCards) {
				setLoadedFailureDefinition(failureDefinitionFromCards);
			} else {
				dispatch(getFailureDefinitionAction(id));
			}
		}
	}, [editingMode, id, failureDefinitionFromCards, dispatch]);

	useEffect(() => {
		if (editingMode && id && individualFailureDefinition.successful && individualFailureDefinition.failureDefinition) {
			if (individualFailureDefinition.failureDefinition.id === id) {
				setLoadedFailureDefinition(individualFailureDefinition.failureDefinition);
			}
		}
	}, [editingMode, id, individualFailureDefinition]);

	const handleSystemInfoChange = React.useCallback(
		(systemType: string, assetType: string, inspectionStandard: string) => {
			if (systemType && assetType) {
				const inspectionParam = systemType === 'WaterDistribution' ? '' : inspectionStandard;

				dispatch(
					fetchFieldMetadataAction({
						type: 'events',
						systemType: systemType,
						assetType: assetType,
						inspectionStandard: inspectionParam,
					}),
				);
				dispatch(
					fetchFieldMetadataAction({
						type: 'tasks',
						systemType: systemType,
						assetType: assetType,
						inspectionStandard: inspectionParam,
					}),
				);

				if (inspectionStandard && systemType !== 'WaterDistribution') {
					dispatch(fetchInspectionStandardCodes(inspectionStandard));
				}
			}
		},
		[dispatch],
	);

	// Clear metadata when navigating between failure definitions
	useEffect(() => {
		// Clear metadata immediately when switching to a different failure definition
		dispatch(clearFieldMetadataAction());

		return () => {
			// Cleanup when navigating away
			dispatch(clearFieldMetadataAction());
		};
	}, [id, dispatch]);

	const eventTypeInformation = selectEventTypes();
	const taskTypeInformation = selectTaskTypes();
	const rehabMetadataLoading = selectRehabMetadataLoading();

	// Convert snake_case to camelCase for field names
	const toCamelCase = (str: string): string => {
		return str.replaceAll(/_([a-z])/g, (_match, letter) => letter.toUpperCase());
	};

	// Transform loaded failure definition to form values (after metadata is available)
	useEffect(() => {
		if (editingMode && id && loadedFailureDefinition) {
			const fd = loadedFailureDefinition;

			if (fd) {
				// Transform events to form format
				const events = fd.events?.map((e: { type: string; date_field: string }) => e.type) || [];
				const eventDateMap: Record<string, string> = {};
				fd.events?.forEach((e: { type: string; date_field: string }) => {
					eventDateMap[e.type] = toCamelCase(e.date_field);
				});

				// Transform tasks to form format
				const tasks = fd.tasks?.map((t: { type: string; date_field: string }) => t.type) || [];
				const taskDateMap: Record<string, string> = {};
				fd.tasks?.forEach((t: { type: string; date_field: string }) => {
					taskDateMap[t.type] = toCamelCase(t.date_field);
				});

				// Transform inspections to form format
				const inspections: string[] = [];
				let conditionScoreKey = '';
				let conditionScoreOperator = '';
				let conditionScoreValue = 0;
				let conditionScoreValueTo = 0;
				let defectCodes: string[] = [];

				if (fd.inspections?.condition_score) {
					inspections.push('conditionScore');
					const dbFieldName = fd.inspections.condition_score.condition_category;
					const fieldMappings = fd.inspectionStandard === 'PACP' ? pacpFieldMappings : wsaFieldMappings;
					const fieldMappingEntry = Object.entries(fieldMappings).find(
						([, mapping]) => mapping.dbFieldName === dbFieldName,
					);
					conditionScoreKey = fieldMappingEntry ? fieldMappingEntry[0] : dbFieldName;
					conditionScoreOperator = fd.inspections.condition_score.operator;
					conditionScoreValue = fd.inspections.condition_score.values[0] || 0;
					conditionScoreValueTo = fd.inspections.condition_score.values[1] || 0;
				}

				if (fd.inspections?.defect_codes && fd.inspections.defect_codes.length > 0) {
					inspections.push('defectCodes');
					defectCodes = fd.inspections.defect_codes;
				}

				const transformedModel: FailureDefinitionFormValues = {
					id: fd.id,
					failureDefinitionName: fd.name,
					systemType: fd.systemType,
					assetType: fd.assetType,
					inspectionStandard: fd.inspectionStandard || 'PACP',
					inspections,
					events,
					eventDateMap,
					tasks,
					taskDateMap,
					conditionScoreKey,
					conditionScoreOperator,
					conditionScoreValue,
					conditionScoreValueTo,
					defectCodes,
					prioritizedCategory: fd.prioritizedCategory || '',
				};

				setModel(transformedModel);
			}
		}
	}, [editingMode, id, loadedFailureDefinition]);

	const inspectionOptions = [
		{
			key: 'defectCodes',
			value: 'Defect Codes',
		},
		{
			key: 'conditionScore',
			value: 'Condition Score',
		},
	];

	const defectCodeTypes = selectStandardCodes(model?.inspectionStandard || 'PACP');

	const isMetadataLoading = React.useMemo(() => {
		if (!editingMode && !createdFailureDefinitionId) {
			return false;
		}

		if (editingMode && id) {
			const hasFailureDefinition = loadedFailureDefinition !== null;
			const isLoading = individualFailureDefinition.inProcess;

			const needsLoading =
				!hasFailureDefinition &&
				(isLoading ||
					(!failureDefinitionFromCards &&
						(!individualFailureDefinition.successful || individualFailureDefinition.failureDefinition?.id !== id)));

			if (needsLoading) {
				return true;
			}

			// Don't show loading for events/tasks fetch if FD is already loaded
			// This prevents infinite loop when component remounts after fetch
			return false;
		}

		return rehabMetadataLoading;
	}, [
		editingMode,
		createdFailureDefinitionId,
		id,
		loadedFailureDefinition,
		individualFailureDefinition.inProcess,
		individualFailureDefinition.successful,
		individualFailureDefinition.failureDefinition,
		failureDefinitionFromCards,
		rehabMetadataLoading,
	]);

	const isLoading = isMetadataLoading;
	const isWaiting = false;
	const isCreating = false;

	useEffect(() => {
		if (!editingMode && createdFailureDefinitionId && urlBasedCreating) {
			history.replace(addRouteNamespace(`failure-definition/${createdFailureDefinitionId}`));
			setEditingMode(true);
			dispatch(getFailureDefinitionAction(createdFailureDefinitionId));
			dispatch(listAllFailureDefinitions());
			setCurrentTabIndex(1);
			setHasAutoSwitchedToResults(true);
		}
	}, [editingMode, createdFailureDefinitionId, urlBasedCreating, history, dispatch]);

	useEffect(() => {
		if (
			loadedFailureDefinition &&
			loadedFailureDefinition.lastRunId &&
			!hasAutoSwitchedToResults &&
			!createdFailureDefinitionId
		) {
			setCurrentTabIndex(1);
			setHasAutoSwitchedToResults(true);
		}
	}, [loadedFailureDefinition, hasAutoSwitchedToResults, createdFailureDefinitionId]);

	const validateForm = (values: FormikValues) => {
		const errors: { [k: string]: string | { [j: string]: string } } = {};
		if (isBlankName(values.failureDefinitionName)) {
			errors.failureDefinitionName = 'Required';
		} else if (!isAlphanumericName(values.failureDefinitionName)) {
			errors.failureDefinitionName = t('Only alphanumeric characters are allowed');
		} else if (
			isDuplicateName(
				values.failureDefinitionName,
				failureDefinitionsCards?.failureDefinitions?.map(fd => fd.name) ?? [],
				editingMode ? loadedFailureDefinition?.name : undefined,
			)
		) {
			errors.failureDefinitionName = t('Enter a unique name');
		}
		if (!values.systemType) errors.systemType = 'Required';
		if (!values.assetType) errors.assetType = 'Required';
		// Only require inspectionStandard if systemType is not WaterDistribution
		if (values.systemType !== 'WaterDistribution' && !values.inspectionStandard) errors.inspectionStandard = 'Required';

		if (values.inspections?.includes('conditionScore')) {
			if (!values.conditionScoreKey) errors.conditionScoreKey = 'Required';
			if (!values.conditionScoreOperator) errors.conditionScoreOperator = 'Required';
			if (
				values.conditionScoreValue === null ||
				values.conditionScoreValue === undefined ||
				values.conditionScoreValue === ''
			) {
				errors.conditionScoreValue = 'Required';
			}
			if (
				values.conditionScoreOperator === 'BETWEEN' &&
				(values.conditionScoreValueTo === null ||
					values.conditionScoreValueTo === undefined ||
					values.conditionScoreValueTo === '')
			) {
				errors.conditionScoreValueTo = 'Required';
			}
		}

		const hasEvents = Array.isArray(values.events) && values.events.length > 0;
		const hasInspections = Array.isArray(values.inspections) && values.inspections.length > 0;
		const hasTasks = Array.isArray(values.tasks) && values.tasks.length > 0;

		if (!hasEvents && !hasInspections && !hasTasks) {
			errors._form = 'At least one of Events, Inspections, or Tasks must be specified.';
		}

		if (values.prioritizedCategory) {
			if (values.prioritizedCategory === 'event' && !hasEvents) {
				errors.prioritizedCategory = 'Events is prioritized but no events are selected.';
			} else if (values.prioritizedCategory === 'inspection' && !hasInspections) {
				errors.prioritizedCategory = 'Inspections is prioritized but no inspections are selected.';
			} else if (values.prioritizedCategory === 'task' && !hasTasks) {
				errors.prioritizedCategory = 'Tasks is prioritized but no tasks are selected.';
			}
		}

		if (Array.isArray(values.events)) {
			errors.eventDateMap = {};

			values.events.forEach((event: string) => {
				if (!values.eventDateMap || !values.eventDateMap[event]) {
					if (!errors.eventDateMap) errors.eventDateMap = {};
					(errors.eventDateMap as { [k: string]: string })[event] = 'Select a date for this event';
				}
			});

			// Remove eventDateMap if there are no errors
			if (Object.keys(errors.eventDateMap).length === 0) {
				delete errors.eventDateMap;
			}
		}
		return errors;
	};

	const handleSubmit = (values: FormikValues, { setSubmitting, setFieldError }: FormikHelpers<FormikValues>) => {
		setSubmitting(true);

		const inspections: Inspections = {};
		if (values.inspections?.includes('conditionScore')) {
			const conditionScoreValues =
				values.conditionScoreOperator === 'BETWEEN'
					? [values.conditionScoreValue, values.conditionScoreValueTo]
					: [values.conditionScoreValue];

			// Get the dbFieldName from local mappings instead of using the key directly
			const fieldMappings = values.inspectionStandard === 'PACP' ? pacpFieldMappings : wsaFieldMappings;
			const fieldMapping = fieldMappings[values.conditionScoreKey];
			const dbFieldName = fieldMapping ? fieldMapping.dbFieldName : values.conditionScoreKey;

			inspections.condition_score = {
				condition_category: dbFieldName,
				operator: values.conditionScoreOperator,
				values: conditionScoreValues,
			};
		}
		if (values.inspections?.includes('defectCodes')) {
			inspections.defect_codes = values.defectCodes;
		}
		const hasValidInspections = Object.keys(inspections).length > 0;

		const tasks =
			Array.isArray(values.tasks) && values.tasks.length > 0
				? values.tasks.map((task: string) => ({
						type: task,
						date_field: values.taskDateMap[task],
				  }))
				: undefined;

		const events =
			Array.isArray(values.events) && values.events.length > 0
				? values.events.map((event: string) => ({
						type: event,
						date_field: values.eventDateMap[event],
				  }))
				: undefined;

		const failureDefinitionPayload: CreateFailureDefinitionRequest = {
			name: values.failureDefinitionName,
			system_type: values.systemType,
			asset_type: values.assetType,
			inspection_standard_type: values.inspectionStandard,

			...(hasValidInspections && { inspections }),
			...(tasks ? { tasks } : {}),
			...(events ? { events } : {}),
			...(values.prioritizedCategory ? { prioritized_category: values.prioritizedCategory } : {}),
		};

		const isUpdate = editingMode && id && id !== 'new';

		const upsertPayload = isUpdate
			? { ...failureDefinitionPayload, failure_definition_id: id }
			: failureDefinitionPayload;

		dispatch(
			upsertFailureDefinition({
				request: upsertPayload,
				onSuccess: (resultId: string) => {
					if (isUpdate) {
						dispatch(listAllFailureDefinitions());
						dispatch(getFailureDefinitionAction(resultId));
					} else {
						setCreatedFailureDefinitionId(resultId);
					}
					setSubmitting(false);
				},
				onError: (message: string) => {
					setFieldError('failureDefinitionName', message);
					setSubmitting(false);
				},
			}),
		);
	};

	return (
		<StylovyzeFormV2 initialValues={model} enableReinitialize validate={validateForm} onSubmit={handleSubmit}>
			{({ values, setFieldValue, validateForm, submitForm, isSubmitting, setSubmitting, setFieldError }) => {
				React.useEffect(() => {
					if (isSubmitting && createdFailureDefinitionId) {
						setSubmitting(false);
					}
				}, [isSubmitting, createdFailureDefinitionId, setSubmitting]);

				const handleRename = (saved: boolean, newName: string) => {
					setIsRenameOpen(false);
					if (!saved || newName === values.failureDefinitionName) {
						return;
					}

					if (editingMode && id && loadedFailureDefinition) {
						const currentDefinition: CreateFailureDefinitionRequest = {
							name: loadedFailureDefinition.name,
							system_type: loadedFailureDefinition.systemType,
							asset_type: loadedFailureDefinition.assetType,
							inspection_standard_type: loadedFailureDefinition.inspectionStandard,
							events: loadedFailureDefinition.events || [],
							tasks: loadedFailureDefinition.tasks || [],
							...(loadedFailureDefinition.inspections?.condition_score ||
							loadedFailureDefinition.inspections?.defect_codes
								? {
										inspections: {
											...(loadedFailureDefinition.inspections?.condition_score
												? { condition_score: loadedFailureDefinition.inspections.condition_score }
												: {}),
											...(loadedFailureDefinition.inspections?.defect_codes
												? { defect_codes: loadedFailureDefinition.inspections.defect_codes }
												: {}),
										},
								  }
								: {}),
							...(loadedFailureDefinition.prioritizedCategory
								? { prioritized_category: loadedFailureDefinition.prioritizedCategory }
								: {}),
						};

						dispatch(
							renameFailureDefinition({
								id,
								newName,
								currentDefinition,
								onSuccess: () => setFieldValue('failureDefinitionName', newName),
								onError: (message: string) => {
									setFieldError('failureDefinitionName', message);
								},
							}),
						);
					} else {
						setFieldValue('failureDefinitionName', newName);
					}
				};

				return (
					<>
						<RenameDialog open={isRenameOpen} currentName={values.failureDefinitionName} onClose={handleRename} />
						<FullPageWrapper
							className={isLoading || isWaiting || isCreating ? 'wait' : ''}
							title={values.failureDefinitionName}
							minorHeader={
								<IconButton size="small" onClick={() => setIsRenameOpen(true)} aria-label="Rename">
									<EditIcon fontSize="small" />
								</IconButton>
							}
							breadcrumbs={
								!editingMode ? ROUTES.failureDefinitionNew.breadcrumbs : ROUTES.failureDefinitionEdit.breadcrumbs
							}
							headerUnderline
							secondary={
								currentTabIndex === 0 ? (
									<Button
										type="submit"
										variant="contained"
										color="primary"
										disabled={isRunning}
										onClick={async () => {
											const errors = await validateForm();
											const isValid = Object.keys(errors).length === 0;
											if (isValid) {
												submitForm();
											}
										}}>
										{isRunning ? t('Running') : t('Run')}
									</Button>
								) : (
									<></>
								)
							}
							secondaryNav={{
								items: [
									{
										title: t('Configuration'),
										content: isMetadataLoading ? (
											<Stack spacing={3}>
												<Text>Loading configuration...</Text>
											</Stack>
										) : (
											<FailureDefinitionConfiguration
												eventTypeInformation={eventTypeInformation}
												taskTypeInformation={taskTypeInformation}
												inspectionOptions={inspectionOptions}
												defectCodeTypes={defectCodeTypes}
												isCreating={!editingMode}
												rehabMetadataLoading={rehabMetadataLoading}
												onSystemInfoChange={handleSystemInfoChange}
											/>
										),
									},
									{
										title: t('Results'),
										content: <FailureDefinitionResultsTab failureDefinition={loadedFailureDefinition} />,
									},
								],
								itemIndex: currentTabIndex,
								itemIndexChanged: (index: number) => setCurrentTabIndex(index),
							}}
							applyPadding={true}></FullPageWrapper>
					</>
				);
			}}
		</StylovyzeFormV2>
	);
};
